import { supabase } from '@/integrations/supabase/client';
import type { FreightContext } from '@/utils/deliveryContext';

export interface TollQuote {
  /** Valor final (já considerando ida/volta e teto) em BRL. */
  amount: number;
  /** Origem do cálculo. */
  source: 'google' | 'cache' | 'disabled' | 'unavailable';
  /** Distância da rota em metros, quando disponível (Google Routes). */
  distanceMeters: number | null;
}

const EMPTY: TollQuote = { amount: 0, source: 'disabled', distanceMeters: null };

/**
 * Consulta o pedágio estimado no servidor (Google Routes API + cache de 30 dias).
 * Nunca lança: qualquer falha devolve zero para não bloquear a jornada.
 */
export async function fetchTollQuote(
  supplierId: string,
  coords: { lat: number; lng: number } | null | undefined,
  context: FreightContext = 'event',
): Promise<TollQuote> {
  if (!supplierId || !coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lng)) {
    return EMPTY;
  }

  try {
    const { data, error } = await supabase.functions.invoke('calcular-pedagio', {
      body: { supplier_id: supplierId, lat: coords.lat, lng: coords.lng, context },
    });
    if (error) return { amount: 0, source: 'unavailable', distanceMeters: null };

    const payload = data as {
      pedagio?: number;
      origem?: TollQuote['source'];
      distancia_metros?: number | null;
    } | null;
    const value = Number(payload?.pedagio);
    const distance =
      payload?.distancia_metros != null && Number.isFinite(Number(payload.distancia_metros))
        ? Number(payload.distancia_metros)
        : null;

    return {
      amount: Number.isFinite(value) && value > 0 ? Math.round(value * 100) / 100 : 0,
      source: payload?.origem ?? 'unavailable',
      distanceMeters: distance,
    };
  } catch {
    return { amount: 0, source: 'unavailable', distanceMeters: null };
  }
}
